import React from 'react';
import {
    KanbanBoard,
    Table,
    Tabs,
    Text,
    ViewToggle,
    mergeClassNames,
    EmptyState,
} from '@clockout-dev/design-elements';
import IssueStatusPill from '../../common/issueStatusPill/IssueStatusPill';
import IssueTypeTag from '../../common/issueTypeTag/IssueTypeTag';
import DateTimeCell from '../../reports/elements/DateTimeCell';
import { IssueLoadFailed, IssueSyncAge, IssueSyncUnavailable, isIssueSyncUnavailable } from './IssueSyncState';
import IssueKanbanCard from './IssueKanbanCard';
import {
    ISSUE_KANBAN_COPY,
    ISSUE_STATUS_CHIPS,
    ISSUE_STATUS_FILTER_ALL,
    ISSUE_TABLE_COPY,
    ISSUE_VIEWS,
    issueSummary,
} from '../issueTracker.constants';
import { DATE_TIME_SECONDS_FORMAT, TIMEZONE_NOTE } from '../../../utils/datetime';
import { AppstoreOutlined, UnorderedListOutlined } from '@ant-design/icons';
import tableStyles from '../../treasury/elements/styles/transactionsTable.module.scss';
import styles from '../styles/jira-issues-table.module.scss';

// The icons are JSX, so these live here rather than in the constants file,
// which states that it imports no JSX so any consumer (including a test) can
// import it safely. They are decorative: the label is always visible.
const VIEW_OPTIONS = [
    { key: ISSUE_VIEWS.KANBAN, label: ISSUE_TABLE_COPY.VIEW_KANBAN, icon: <AppstoreOutlined /> },
    { key: ISSUE_VIEWS.TABLE, label: ISSUE_TABLE_COPY.VIEW_TABLE, icon: <UnorderedListOutlined /> },
];

/**
 * "What the Clockout team is working on" — the FI's read-only view of the
 * mirrored issue tracker, as a table or as a board.
 *
 * Presentational: every value arrives as a prop and every callback goes back to
 * the controller. Nothing here calls the API.
 *
 * Both views page SERVER-SIDE, in different shapes: `rows` is one page of the
 * table, while the board's columns each hold their own pages and load more as
 * they are scrolled. Neither view can be rendered from the other's data, which
 * is why switching view triggers a load rather than a re-slice.
 *
 * @param {Object} props
 * @param {object[]} props.rows one server page of the table
 * @param {Array<{key: string, label: string}>} props.columns board column defs
 * @param {(statusKey: string) => object} props.columnState one column's paging state
 * @param {(statusKey: string) => void} props.onColumnEndReached
 * @param {string} props.view `table` | `kanban`
 * @param {(view: string) => void} props.onViewChange
 * @param {boolean} props.loading
 * @param {boolean} props.error request itself failed, as opposed to a bad mirror
 * @param {number} props.perPage fallback page size before the first response
 * @param {object|null} props.pagination server page info for the table
 * @param {object|null} props.sync `{ last_ok_at, last_run_status, stale }`
 * @param {string} props.statusFilter
 * @param {(key: string) => void} props.onStatusFilterChange
 * @param {(pagination: object) => void} props.onChange
 * @param {(record: object) => void} props.onRowClick
 * @param {() => void} props.onRetry reload the active view after a failed request
 * @param {string|null} props.selectedKey
 * @param {(statusKey: string) => object|undefined} props.resolveStatusEntry served label lookup
 */
function JiraIssuesTable({
    rows,
    columns,
    columnState,
    onColumnEndReached,
    view,
    onViewChange,
    loading,
    error,
    perPage,
    pagination,
    sync,
    statusFilter,
    onStatusFilterChange,
    onChange,
    onRowClick,
    onRetry,
    selectedKey,
    resolveStatusEntry,
}) {
    const dataSource = rows || [];

    const pageInfo = pagination
        ? {
              current: pagination.current,
              page: pagination.current,
              perPage: pagination.pageSize,
              total: pagination.total,
          }
        : { perPage, total: dataSource.length };

    // Two reasons not to draw anything, and they are NOT the same as "no rows":
    // the request failed, or the mirror has never completed a run. Either way an
    // empty grid or a board of empty columns would read as "nothing is being
    // worked on".
    //
    // They are kept apart rather than collapsed into one flag. A failed request
    // arrives with `sync: null` — the delegate clears it, because a previous
    // page's freshness says nothing about a page we never got — so asking the
    // unavailable banner to also cover the error case rendered NOTHING at all:
    // it early-returns on a null sync. Each cause now has its own banner, and
    // only the failure offers a retry.
    const mirrorUnavailable = !error && isIssueSyncUnavailable(sync);
    const unavailable = error || mirrorUnavailable;

    const columnDefs = [
        {
            title: ISSUE_TABLE_COPY.COLUMN_KEY,
            dataIndex: 'key',
            key: 'key',
            width: 110,
            render: (value) => (
                <Text as="span" size="xs" className={styles.keyCell}>
                    {value}
                </Text>
            ),
        },
        {
            title: ISSUE_TABLE_COPY.COLUMN_SUMMARY,
            dataIndex: 'summary',
            key: 'summary',
            ellipsis: true,
            render: (value) => (
                <Text as="span" size="xs" className={styles.summaryCell}>
                    {issueSummary(value)}
                </Text>
            ),
        },
        {
            title: ISSUE_TABLE_COPY.COLUMN_TYPE,
            dataIndex: 'issueType',
            key: 'issueType',
            width: 100,
            render: (value) => <IssueTypeTag type={value} />,
        },
        {
            title: ISSUE_TABLE_COPY.COLUMN_STATUS,
            dataIndex: 'status',
            key: 'status',
            width: 140,
            render: (value) => <IssueStatusPill status={value} servedEntry={resolveStatusEntry?.(value)} />,
        },
        {
            title: ISSUE_TABLE_COPY.COLUMN_ASSIGNEE,
            dataIndex: 'assignee',
            key: 'assignee',
            width: 150,
            render: (value) => (
                <Text as="span" size="xs" className={tableStyles.idCell}>
                    {value || ISSUE_TABLE_COPY.UNASSIGNED}
                </Text>
            ),
        },
        {
            title: ISSUE_TABLE_COPY.COLUMN_CREATED,
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 205,
            // Two things keep a timestamp inside its column:
            //  - `showTimeZone={false}`: the zone is stated ONCE under the table,
            //    so nine cells do not each carry a "GMT-4" they have no room for.
            //  - `styles.dateCell`: DateTimeCell is `white-space: nowrap`, which
            //    means an under-sized column does not clip, it PAINTS OVER the
            //    next one. Allowing a wrap makes a squeezed column go to two
            //    lines instead of over its neighbour.
            render: (value) => (
                <DateTimeCell
                    value={value}
                    format={DATE_TIME_SECONDS_FORMAT}
                    showTimeZone={false}
                    className={styles.dateCell}
                />
            ),
        },
        {
            title: ISSUE_TABLE_COPY.COLUMN_STARTED,
            dataIndex: 'startedAt',
            key: 'startedAt',
            width: 205,
            // `null` is a fact the server asserts (work has not begun), so it
            // reads as "Not started" rather than as a dash meaning "unknown".
            render: (value) => (
                <DateTimeCell
                    value={value}
                    format={DATE_TIME_SECONDS_FORMAT}
                    showTimeZone={false}
                    neverLabel={ISSUE_TABLE_COPY.NOT_STARTED}
                    className={styles.dateCell}
                />
            ),
        },
        {
            title: ISSUE_TABLE_COPY.COLUMN_UPDATED,
            dataIndex: 'lastUpdatedAt',
            key: 'lastUpdatedAt',
            width: 205,
            render: (value) => (
                <DateTimeCell
                    value={value}
                    format={DATE_TIME_SECONDS_FORMAT}
                    showTimeZone={false}
                    className={styles.dateCell}
                />
            ),
        },
    ];

    return (
        <section className={styles.panel}>
            <div className={styles.controls}>
                {/* The age sits BESIDE the toggle, not inside it: the toggle is a
                    `radiogroup`, and a timestamp nested in one is announced as a
                    third option. */}
                <div className={styles.controlsRight}>
                    <ViewToggle
                        options={VIEW_OPTIONS}
                        value={view}
                        onChange={onViewChange}
                        label={ISSUE_TABLE_COPY.VIEW_TOGGLE_LABEL}
                    />
                    <IssueSyncAge sync={sync} />
                </div>
                {/* Table only. On the board the COLUMNS are the statuses, so a
                    status filter would just be a one-column board — the chips
                    would be asking a question the layout already answers. */}
                {view === ISSUE_VIEWS.TABLE ? (
                    /* No `searchParamKey`. Tabs writes the key VERBATIM after
                       calling onChange, which put `?issueStatus=all` back in the
                       URL every time the filter was cleared — the one thing the
                       constant says never happens. The strip is fully controlled
                       by `statusFilter`, so the controller owns the param on its
                       own: it writes a real status and deletes the sentinel. */
                    <Tabs
                        activeKey={statusFilter}
                        onChange={onStatusFilterChange}
                        items={ISSUE_STATUS_CHIPS.map(({ key, label }) => ({ key, label }))}
                    />
                ) : null}
            </div>

            {/* The error takes the space the data would have occupied, rather
                than sitting above an empty one. */}
            {error ? <IssueLoadFailed onRetry={onRetry} /> : null}
            {mirrorUnavailable ? <IssueSyncUnavailable sync={sync} /> : null}
            {unavailable ? null : (
                <>
                    {view === ISSUE_VIEWS.KANBAN ? (
                        // The board is design-elements' `KanbanBoard`: layout
                        // and per-column paging are not Jira's to own, and
                        // clockout-staff draws the same board over the same
                        // mirror. What stays here is the CARD, which is the only
                        // Jira-shaped part of it.
                        <KanbanBoard
                            columns={columns || []}
                            columnState={columnState}
                            selectedKey={selectedKey}
                            onCardClick={onRowClick}
                            onColumnEndReached={onColumnEndReached}
                            copy={ISSUE_KANBAN_COPY}
                            renderCard={(issue) => <IssueKanbanCard issue={issue} />}
                        />
                    ) : (
                        <Table
                            rowKey="key"
                            dataSource={dataSource}
                            loading={loading}
                            columns={columnDefs}
                            // Server-paginated: `rows` is ONE page, and `total`
                            // is the server's count, so antd draws the right
                            // number of pages without holding them.
                            pageInfo={pageInfo}
                            saveSearchParams
                            onChange={onChange}
                            onRow={(record) => ({ onClick: () => onRowClick?.(record) })}
                            rowClassName={(record) =>
                                record?.key && record.key === selectedKey ? styles.rowSelected : ''
                            }
                            emptyState={<EmptyState title={statusFilter === ISSUE_STATUS_FILTER_ALL
                                    ? ISSUE_TABLE_COPY.EMPTY_ALL
                                    : ISSUE_TABLE_COPY.EMPTY_FILTERED} />}
                            classNames={{
                                shell: tableStyles.tableWrapper,
                                root: mergeClassNames([tableStyles.table, styles.clickableRows]),
                            }}
                        />
                    )}
                    <Text as="p" size="xs" className={styles.timezoneNote}>
                        {TIMEZONE_NOTE}
                    </Text>
                </>
            )}
        </section>
    );
}

export default JiraIssuesTable;
